/**
 * Subscribable interface - a function that takes callbacks and returns an unsubscribe function.
 */
export type Subscribable<T> = (
	onValue: (value: T) => void,
	onError?: (error: unknown) => void,
	onComplete?: () => void
) => () => void;

const COMPLETE = Symbol("COMPLETE");

/**
 * Transforms a Subscribable<T> into an AsyncIterableIterator<T>.
 *
 * THEORY OF OPERATION.
 *
 * A Subscribable is an implementation of the publish/subscribe design pattern. When subscribers
 * subscribe, they receive notifications of events eagerly when they are produced. Because events
 * are processed eagerly, we say Subscribable is a "push-based" eventing semantics.
 *
 * The EcmaScript standard AsyncIterableIterator<T>, in contrast, is "pull-based"—events are
 * represented by a sequence of Promises which completely suspend the thread and resolve only when a
 * new value becomes available.
 *
 * To transform a Subscribable<T> into an AsyncIterableIterator<T>, something needs to sit between
 * the two and act as a bridge. This bridge will:
 *
 *   - Hold a Promise open until the Subscribable emits a value.
 *   - If the Subscribable produces many values before they can be consumed, "buffer" them in a
 *     PromiseWithResolvers[] so they can be consumed one after another by the
 *     AsyncIterableIterator<T> consumer.
 *   - Handle all errors, completion events, and abort signals.
 *
 * To accomplish this, this function does the following, in this order.
 *
 *   1. Initialize a PromiseWithResolvers[]. A PromiseWithResolvers is basically a Promise that
 *      blocks until the Subscribable publishes a value t and we call resolvers.resolve(t). The
 *      AsyncIterableIterator<T> is simply waiting for this Promise to resolve, and once it does, it
 *      instantly yields the value to its consumer.
 *   2. Subscribe to the Subscribable. Each time the Subscribable produces a value t, we call
 *      resolvers.resolve(t), causing the AsyncIterableIterator<T> to advance.
 *   3. Loop through the PromiseWithResolvers[], awaiting each one to resolve and yielding its
 *      value. The yield and await are what makes this an AsyncIterableIterator<T>.
 *   4. On completion, error, or abort, unsubscribe and return.
 *
 * @param subscribe - Function that subscribes to value changes, returns unsubscribe
 * @param signal - Optional AbortSignal to stop iteration
 */
export async function* subscribableToAsyncGenerator<T>(
	subscribe: Subscribable<T>,
	signal?: AbortSignal
): AsyncIterableIterator<T> {
	type QueueItem = PromiseWithResolvers<T | typeof COMPLETE>;

	let unsubscribe: (() => void) | undefined;
	let done = false;
	const queue: QueueItem[] = [Promise.withResolvers()];

	const handleAbort = () => {
		if (done) {
			return;
		}
		done = true;
		queue[queue.length - 1]?.resolve(COMPLETE);
		unsubscribe?.();
	};

	try {
		if (signal?.aborted) {
			return;
		}

		signal?.addEventListener("abort", handleAbort, { once: true });

		unsubscribe = subscribe(
			(data) => {
				if (done) {
					return;
				}
				const n = queue[queue.length - 1];
				queue.push(Promise.withResolvers());
				n?.resolve(data);
			},
			(err) => {
				if (done) {
					return;
				}
				done = true;
				queue[queue.length - 1]?.reject(err);
			},
			() => {
				if (done) {
					return;
				}
				done = true;
				queue[queue.length - 1]?.resolve(COMPLETE);
			}
		);

		while (true) {
			const n = queue[0];
			if (!n) {
				break;
			}

			const value = await n.promise;
			void queue.shift();

			if (value === COMPLETE) {
				break;
			}

			yield value;
		}
	} finally {
		signal?.removeEventListener("abort", handleAbort);
		unsubscribe?.();
	}
}
